import React, { useCallback } from "react";
import useSWR, { mutate, type SWRConfiguration } from "swr";

// SWR-based implementation of useServerState for workspace feature
// Provides caching, revalidation, and error handling via SWR

export interface UseServerStateOptions<T> {
  key: unknown[];
  fetcher: (data?: any, signal?: AbortSignal) => Promise<T>;
  manual?: boolean;
  enabled?: boolean;
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
  config?: SWRConfiguration<T>;
}

export interface UseServerStateResult<T> {
  data: T | null;
  error: Error | null;
  isLoading: boolean;
  trigger: (data?: any) => Promise<T>;
  refetch: () => Promise<T>;
}

export function useServerState<T>({
  key,
  fetcher,
  manual = false,
  enabled = true,
  onSuccess,
  onError,
  config,
}: UseServerStateOptions<T>): UseServerStateResult<T> {
  // Create a stable key for SWR
  const swrKey = React.useMemo(() => {
    if (!enabled || manual) return null;
    return JSON.stringify(key);
  }, [key, enabled, manual]);

  // SWR fetcher wrapper that handles our fetcher format
  const swrFetcher = useCallback(
    async (keyString: string) => {
      const parsedKey = JSON.parse(keyString);
      const controller = new AbortController();
      return fetcher(undefined, controller.signal);
    },
    [fetcher]
  );

  // Use SWR for data fetching
  const {
    data,
    error,
    isLoading,
    mutate: swrMutate,
  } = useSWR<T>(swrKey, swrFetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    shouldRetryOnError: true,
    ...config,
    onSuccess: (data, key, swrConfig) => {
      onSuccess?.(data);
      config?.onSuccess?.(data, key, swrConfig);
    },
    onError: (error, key, swrConfig) => {
      const err = error instanceof Error ? error : new Error(String(error));
      onError?.(err);
      config?.onError?.(error, key, swrConfig);
    },
  });

  // Manual trigger function
  const trigger = useCallback(
    async (triggerData?: any): Promise<T> => {
      try {
        const controller = new AbortController();
        const result = await fetcher(triggerData, controller.signal);

        // Update SWR cache with the new data
        const cacheKey = JSON.stringify(key);
        mutate(cacheKey, result, false);

        onSuccess?.(result);
        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        onError?.(error);
        throw error;
      }
    },
    [fetcher, key, onSuccess, onError]
  );

  // Refetch function using SWR's mutate
  const refetch = useCallback(async (): Promise<T> => {
    const result = await swrMutate();
    if (result === undefined) {
      throw new Error("Failed to refetch data");
    }
    return result;
  }, [swrMutate]);

  return {
    data: data ?? null,
    error: error ? (error instanceof Error ? error : new Error(String(error))) : null,
    isLoading: isLoading ?? false,
    trigger,
    refetch,
  };
}
